import { useState, useRef, useEffect } from 'react';
import { Bell, Clock, CheckCircle, ExternalLink, Upload } from 'lucide-react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@edison/api-client-react';
import { cn } from '@/lib/utils.js';

interface NotificationItem {
  id: string;
  icon: typeof Clock;
  iconVariant: 'indigo' | 'amber' | 'green';
  title: string;
  sub: string;
  href: string;
  unread: boolean;
}

function useNotifications(): { items: NotificationItem[]; isLoading: boolean } {
  // ERA-019: use the ONE canonical pending count (/time-entries/summary totals — pending status
  // only, no_rd/archived/deleted projects excluded), the same definition as the sidebar badge,
  // dashboard and chatbot. Previously this read the raw /time-entries pagination total, which
  // included no_rd/archived projects and disagreed with every other "pending" surface.
  const { data: pendingTotals, isLoading } = useQuery({
    queryKey: ['time-entries', 'summary', 'pending-notification'],
    queryFn: () =>
      axiosInstance
        .get<{ data: { totals: { pendingCount: number } } }>('/api/time-entries/summary')
        .then((r) => r.data.data.totals),
  });

  const { data: pendingUploads } = useQuery({
    queryKey: ['artefacts', 'pending_confirmation'],
    queryFn: () =>
      axiosInstance
        .get<{ data: { items: Array<{ id: string; title: string; uploadFilename?: string | null }> } }>(
          '/api/artefacts',
          { params: { uploadStatus: 'pending_confirmation' } },
        )
        .then((r) => r.data.data.items),
    refetchInterval: 8000,
    refetchIntervalInBackground: false,
  });

  const pendingCount = pendingTotals?.pendingCount ?? 0;

  const pendingUploadCount = pendingUploads?.length ?? 0;

  const items: NotificationItem[] = [];

  if (pendingCount > 0) {
    items.push({
      id: 'pending-time',
      icon: Clock,
      iconVariant: 'amber',
      title: `${pendingCount} time ${pendingCount === 1 ? 'entry' : 'entries'} pending review`,
      sub: 'Approve or reject in the Time queue',
      href: '/timewriting',
      unread: true,
    });
  }

  if (pendingUploadCount > 0) {
    items.push({
      id: 'pending-uploads',
      icon: Upload,
      iconVariant: 'indigo',
      title: `${pendingUploadCount} transcript${pendingUploadCount !== 1 ? 's' : ''} need${pendingUploadCount === 1 ? 's' : ''} review`,
      sub: 'AI extracted — confirm project tags',
      href: '/evidence',
      unread: true,
    });
  }

  return { items, isLoading };
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const { items, isLoading } = useNotifications();
  const [, navigate] = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const visible = items.filter(i => !dismissed.has(i.id));
  const unreadCount = visible.filter(i => i.unread).length;

  const markAllRead = () => setDismissed(new Set(items.map(i => i.id)));

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unreadCount > 0 ? `${unreadCount} new notification${unreadCount > 1 ? 's' : ''}` : 'Notifications'}
        className="relative p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
      >
        <Bell className="h-4 w-4" aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-[oklch(0.62_0.18_25)]"
          />
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className={cn(
            'absolute right-0 top-full mt-2 z-50 w-[360px]',
            'rounded-[--radius-card] bg-card border border-[oklch(0.88_0.006_250)]',
            'flex flex-col overflow-hidden',
          )}
          style={{
            boxShadow: '0 16px 44px oklch(0.15 0.02 250 / 0.20)',
            animation: 'pop 0.16s cubic-bezier(0.16,1,0.3,1) both',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-hairline">
            <span className="text-[13px] font-semibold text-ink">Notifications</span>
            {visible.some(i => i.unread) && (
              <button
                onClick={markAllRead}
                className="text-[11px] text-dim hover:text-ink transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Body */}
          <div className="overflow-y-auto max-h-[320px]">
            {isLoading ? (
              <div className="px-4 py-6 text-center text-[13px] text-dim">Loading…</div>
            ) : visible.length === 0 ? (
              <div className="px-4 py-8 flex flex-col items-center gap-2">
                <div className="h-9 w-9 rounded-[9px] bg-surface-2 flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-[oklch(0.55_0.14_155)]" aria-hidden="true" />
                </div>
                <p className="text-[13px] text-dim">You're all caught up</p>
              </div>
            ) : (
              <ul>
                {visible.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => { setOpen(false); navigate(item.href); }}
                      className={cn(
                        'w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-surface-3 transition-colors',
                        item.unread && 'bg-[oklch(0.985_0.008_262)]',
                      )}
                    >
                      <div className={cn(
                        'h-8 w-8 rounded-[8px] flex items-center justify-center shrink-0 mt-0.5',
                        item.iconVariant === 'amber' && 'bg-[oklch(0.95_0.04_65)]',
                        item.iconVariant === 'indigo' && 'bg-[oklch(0.93_0.02_262)]',
                        item.iconVariant === 'green'  && 'bg-[oklch(0.95_0.03_155)]',
                      )}>
                        <item.icon className={cn(
                          'h-4 w-4',
                          item.iconVariant === 'amber' && 'text-[oklch(0.48_0.12_65)]',
                          item.iconVariant === 'indigo' && 'text-[oklch(0.40_0.08_262)]',
                          item.iconVariant === 'green'  && 'text-[oklch(0.42_0.12_155)]',
                        )} aria-hidden="true" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-ink truncate">{item.title}</p>
                        <p className="text-[11.5px] text-dim truncate mt-0.5">{item.sub}</p>
                      </div>

                      {item.unread && (
                        <span className="h-2 w-2 rounded-full bg-[oklch(0.62_0.18_25)] shrink-0 mt-1.5" aria-hidden="true" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-hairline px-4 py-2.5">
            <button
              onClick={() => { setOpen(false); navigate('/timewriting'); }}
              className="flex items-center gap-1 text-[12px] text-dim hover:text-ink transition-colors"
            >
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
              View all in Time queue
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
